import {
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Spacer,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Tag,
  Text,
} from "@chakra-ui/react";
import { useContext, useState,useEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../App";
import { deleteFollow, postFollow } from "../../api/user/profile/Profile";
import { pushAndNavigate } from "../../native/SwipeBack";
import { scrollbarStyle } from "../../theme/scrollbarStyles";
import { ArrowLeft, MessagesFilled, Plus, Search } from "../icons/icons";
import LazyAvatar from "../lazy/LazyAvatar";
import { SafeArea } from "capacitor-plugin-safe-area";
import useMobile from "../../hooks/useMobile";


interface User {
  id: string;
  name: string;
  avatar: string | null;
  follows?: boolean | null;
  // Render-only (precomputed by buildBookedList callers). Optional so existing
  // callers stay valid. A ChildProfile has no account, so a child row suppresses
  // profile-navigation and the message button. `attribution` holds the locked
  // copy ("with Alex" / "Your kid" / "You") rendered as a quiet secondary label.
  is_child?: boolean;
  attribution?: string;
  // Check-in key (D-05/D-06). When present, check-in reads/writes/emits booking_id;
  // callers that supply no booking_id fall back to user.id so non-check-in paths stay valid.
  booking_id?: string;
}

interface UserSelectModalProps {
  title?: string;
  isMobile?: boolean;
  isOpen: boolean;
  onClose: () => void;
  isAdmin?: boolean;
  users: User[];
  onChange: (selectedUsers: User[]) => void;
  hideButtons?: boolean;

  // The viewer's own member id. Rows matching this id are the viewer's own
  // account, so their follow button is suppressed (you can't follow yourself).
  currentUserId?: string;

  customIcon?: React.ReactElement;

  enableCheckIn?: boolean;
  checkedInIds?: Set<string>;
  onCheckIn?: (bookingId: string, checked: boolean) => void;

  // When provided, the bulk button runs this instead of the default
  // "message every listed user" navigation. Lets callers open a curated group
  // chat (e.g. the whole class: coaches + players, named after the event)
  // rather than only the users shown in this modal. `groupMessageLabel`
  // relabels the button to match.
  onGroupMessage?: () => void;
  groupMessageLabel?: string;
  // Names the resulting group conversation (e.g. the event title). Applied via
  // the ?subject= deep link on the default navigate path; TalkJS only shows it
  // for group chats (2+ participants), so 1-to-1s keep the person's name.
  groupMessageSubject?: string;
}

const UserSelectModal = ({
  isMobile: isMobileProp,
  isOpen,
  onClose,
  hideButtons,
  users,
  isAdmin,
  customIcon,
  title = "All users",
  enableCheckIn,
  checkedInIds,
  onCheckIn,
  onGroupMessage,
  groupMessageLabel,
  groupMessageSubject,
  currentUserId,
}: UserSelectModalProps) => {
  const isMobileDetected = useMobile(768);
  const isMobile = isMobileProp ?? isMobileDetected;
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const [statusBarHeight, setStatusBarHeight] = useState<number>(0);
  const [followOverrides, setFollowOverrides] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const { user: authUser } = useContext(AuthContext);
  // The viewer's own member id: explicit prop wins, otherwise fall back to the
  // logged-in user from context so self-suppression works for every caller
  // without each one having to thread the id through.
  const viewerMemberId = currentUserId ?? authUser?.member_profile?.id;

  const isFollowing = (user: User) =>
    followOverrides[user.id] ?? user.follows ?? false;

  // Recipients for the default "Send group message" action: everyone in the
  // list except kids (ChildProfiles, no real account) and the viewer themselves
  // — you can't start a group chat with only yourself. When a caller supplies
  // its own onGroupMessage (e.g. "Message class", which also folds in coaches),
  // that handler owns the solo-player check instead.
  const groupRecipientIds = (users ?? [])
    .filter((user) => !user.is_child && user.id !== viewerMemberId)
    .map((user) => user.id);
  const showGroupMessage = !!onGroupMessage || groupRecipientIds.length > 0;

  const invalidateEventQueries = () => {
    queryClient.invalidateQueries("upcomingEvents");
    queryClient.invalidateQueries("getVenueEvents");
    queryClient.invalidateQueries("getEventDetail");
    queryClient.invalidateQueries("getEvent");
  };

  const followMutation = useMutation(["follow"], postFollow, {
    onError: (_err, vars: { id: string }) => {
      setFollowOverrides((prev) => ({ ...prev, [vars.id]: false }));
    },
    onSettled: () => {
      invalidateEventQueries();
    },
  });
  const unfollowMutation = useMutation(["unfollow"], deleteFollow, {
    onError: (_err, vars: { id: string }) => {
      setFollowOverrides((prev) => ({ ...prev, [vars.id]: true }));
    },
    onSettled: () => {
      invalidateEventQueries();
    },
  });

  const toggleFollow = (user: User) => {
    const wasFollowing = isFollowing(user);
    setFollowOverrides((prev) => ({ ...prev, [user.id]: !wasFollowing }));
    if (wasFollowing) {
      unfollowMutation.mutate({ id: user.id });
    } else {
      followMutation.mutate({ id: user.id });
    }
  };

  useEffect(() => {
    SafeArea.getStatusBarHeight().then(({ statusBarHeight }) => {
      setStatusBarHeight(statusBarHeight);
    });
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={isMobile ? "full" : "md"}>
      <ModalOverlay />
      <ModalContent p="0">
      <Spacer h={statusBarHeight + "px"}  userSelect="none"/>
        <ModalBody p="20px">
          {isMobile ? (
            <Box w="100%" p="20px 0" zIndex={1} pos="relative">
              <Center pos="absolute" w="100%" h="100%" top="0">
                <Text variant="title" fontSize="lg">
                  {title}
                </Text>
              </Center>
              <IconButton
                minW="32px"
                h="32px"
                variant="light"
                aria-label="back"
                onClick={onClose}
                icon={
                  <Icon width="20px" height="19px" viewBox="0 0 23 20">
                    <ArrowLeft color="#171C22" />
                  </Icon>
                }
              />
            </Box>
          ) : (
            <Text mb="20px" variant="title">
              {title}
            </Text>
          )}

          <InputGroup>
            <InputLeftElement>
              <Icon width="18" height="18" viewBox="0 0 18 18">
                <Search color="#7E8B9A" />
              </Icon>
            </InputLeftElement>
            <Input
              w="100%"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              variant="filled"
              placeholder="Search or start a new chat..."
            />
          </InputGroup>
          <Box
            h={isMobile ? "calc(100vh - 300px)" : "400px"}
            overflowY="auto"
            sx={scrollbarStyle}
            mr="10px"
          >
            {users?.length > 0 &&
              users
                .filter((user) =>
                  user.name.toLowerCase().includes(search.toLowerCase())
                )
                .map((user) => {
                  return (
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      p="10px 0"
                      key={user.booking_id ?? user.id}
                      borderRadius="10px"
                      cursor={user.is_child ? "default" : "pointer"}
                      onClick={() => {
                        // A kid row carries a ChildProfile id, not a member id —
                        // navigating to /user/profile?id=<child_id> 404s, so
                        // suppress profile navigation entirely for kids.
                        if (user.is_child) {
                          return;
                        }
                        // Don't close the modal explicitly: routing away from
                        // /user/events will unmount the parent and the modal
                        // with it. If we closed here via replace:true, the
                        // history entry holding `playersEventId` would be
                        // overwritten and back navigation wouldn't restore
                        // the open modal.
                        if (isAdmin) {
                          navigate("/admin/profile?id=" + user.id);
                        } else {
                          pushAndNavigate(navigate, "/user/profile?id=" + user.id);
                        }
                      }}
                    >
                      <Flex alignItems="center" gap="16px">
                        <LazyAvatar
                          src={user.avatar ?? ""}
                          width={41}
                          height={41}
                          border="1px solid #7E8B9A"
                        />
                        <Box>
                          <Flex alignItems="center" gap="8px">
                            <Text variant="title" fontSize="lg">
                              {user.name}
                            </Text>
                            {user.is_child && (
                              <Tag
                                bg="#F5F7F9"
                                color="#3B4654"
                                fontSize="12px"
                                fontWeight={500}
                                borderRadius="6px"
                                px="8px"
                                py="2px"
                              >
                                Kid
                              </Tag>
                            )}
                          </Flex>
                          {user.attribution && (
                            <Text variant="subtle" fontSize="14px" fontWeight={400}>
                              {user.attribution}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                      <Flex gap="8px" alignItems="center" onClick={(e) => e.stopPropagation()}>
                        {/* A kid row is a ChildProfile, not a real account, and
                            your own row is yourself — neither is followable, so
                            suppress the follow button for both. */}
                        {!isAdmin && !enableCheckIn && !user.is_child && user.id !== viewerMemberId && user.follows !== null && (() => {
                          const following = isFollowing(user);
                          return (
                            <Button
                              variant="heavy"
                              size="sm"
                              bg={following ? '#22C55E' : undefined}
                              px={following ? '14px' : undefined}
                              _hover={following ? { bg: 'green.600' } : undefined}
                              _active={following ? { bg: 'green.600' } : undefined}
                              aria-label={following ? "unfollow user" : "follow user"}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFollow(user);
                              }}
                              rightIcon={
                                !following ? (
                                  <Icon w="14px" h="14px" viewBox="0 0 20 20" color="white" mb="2px">
                                    <Plus />
                                  </Icon>
                                ) : undefined
                              }
                            >
                              {following ? "Following" : "Follow"}
                            </Button>
                          );
                        })()}
                        {/* A kid row is a ChildProfile with no real account,
                            and your own row is yourself — neither is messageable,
                            so suppress the message button for both (mirrors the
                            follow-button gate above). */}
                        {!isAdmin && !user.is_child && user.id !== viewerMemberId && (
                          <IconButton
                            aria-label="message user"
                            variant="light"
                            icon={
                              customIcon ?? (
                                <Icon w="20px" h="20px" viewBox="0 0 24 24">
                                  <MessagesFilled />
                                </Icon>
                              )
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/user/messages?to=" + user.id);
                            }}
                          />
                        )}
                        {/* Check-in control lives on the far right: it's always
                            rendered for every booked row when check-in is on,
                            while the message button is conditional, so anchoring
                            it last keeps the checkboxes column aligned. */}
                        {enableCheckIn && user.booking_id && (
                          <Checkbox
                            variant="checkin"
                            isChecked={checkedInIds?.has(user.booking_id) ?? false}
                            onChange={() => onCheckIn?.(user.booking_id as string, !(checkedInIds?.has(user.booking_id as string) ?? false))}
                            aria-label={`Check in ${user.name}`}
                          />
                        )}
                      </Flex>
                    </Flex>
                  );
                })}
          </Box>
          {!hideButtons && (
            <Flex gap="12px" w="100%">
              <Button
                variant="light"
                size="sm"
                w={showGroupMessage ? "50%" : "100%"}
                onClick={onClose}
              >
                Close
              </Button>
              {showGroupMessage && (
                <Button
                  variant="heavy"
                  size="sm"
                  w="50%"
                  onClick={() => {
                    if (onGroupMessage) {
                      onGroupMessage();
                      onClose();
                      return;
                    }
                    // Navigate away only — do NOT call onClose() here. On some
                    // callers onClose pops history (navigate(-1)), which races
                    // this forward navigation and bounces the user straight back
                    // to the modal (a white flash then the modal reopens). The
                    // route change unmounts the modal anyway, same as the per-row
                    // message button.
                    const params = new URLSearchParams({ to: groupRecipientIds.join(",") });
                    // Name the conversation (event title) only for a real group
                    // (2+ recipients); a 1-to-1 keeps the other person's name.
                    if (groupMessageSubject && groupRecipientIds.length > 1) {
                      params.set("subject", groupMessageSubject);
                    }
                    navigate("/user/messages?" + params.toString());
                  }}
                >
                  {groupMessageLabel ?? "Send group message"}
                </Button>
              )}
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default UserSelectModal;
